import axios, { AxiosHeaders } from 'axios';
import { getApiBaseUrl } from '../utils/apiConfig';
import { getEstablishmentSlugFromUrl, shouldMakeApiCalls } from '../utils/slugHelper';
import { ApiError, isApiError } from '../types/api-error';
import {
  enrichPermissionDeniedError,
  getErrorMessage,
  getResponseInterceptorDecision,
  isMainApiRequest,
} from './apiInterceptorLogic';

interface QueueItem {
  resolve: (value: unknown) => void;
  reject: (reason: unknown) => void;
}

/**
 * API base URL: detectada em runtime pelo hostname (staging vs prod).
 * staging.hivi.com.br → api-staging.hivi.com.br
 * hivi.com.br → api.hivi.com.br
 */
const api = axios.create({
  baseURL: getApiBaseUrl(),
  timeout: 15000,
  withCredentials: true,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Variável para controlar refresh em andamento (evita múltiplas chamadas simultâneas)
let isRefreshing = false;
let failedQueue: QueueItem[] = [];

const processQueue = (error: ApiError | null, token: string | null = null) => {
  failedQueue.forEach((prom) => {
    if (error) {
      prom.reject(error);
    } else {
      prom.resolve(token);
    }
  });

  failedQueue = [];
};

const handleServerError = (error: unknown): Promise<never> => {
  if (!isMainApiRequest(error, getApiBaseUrl())) {
    return Promise.reject(error);
  }
  if (!window.location.pathname.includes('/service-unavailable')) {
    window.location.href = '/service-unavailable';
  }
  return Promise.reject(error);
};

const handlePermissionError = (error: unknown): Promise<never> => {
  enrichPermissionDeniedError(error);
  return Promise.reject(error);
};

const handleRefreshError = (refreshError: unknown): Promise<never> => {
  const errorMessage = getErrorMessage(refreshError);
  console.error('❌ [API Interceptor] Falha ao renovar token:', errorMessage);

  const apiError = isApiError(refreshError) ? refreshError : null;
  processQueue(apiError, null);
  isRefreshing = false;

  const message = isApiError(refreshError)
    ? refreshError.response?.data?.message || 'Sua sessão expirou'
    : 'Sua sessão expirou';

  window.dispatchEvent(
    new CustomEvent('session-expired', {
      detail: {
        message,
        reason: 'refresh_token_expired',
      },
    })
  );

  return Promise.reject(refreshError);
};

interface AxiosRequestConfig {
  _retry?: boolean;
  url?: string;
}

const removeEstablishmentSlugHeader = (headers: unknown): void => {
  if (!headers) return;

  if (headers instanceof AxiosHeaders) {
    headers.delete('x-establishment-slug');
    return;
  }

  if (typeof headers === 'object') {
    delete (headers as Record<string, unknown>)['x-establishment-slug'];
  }
};

const handleTokenRefresh = async (originalRequest: AxiosRequestConfig): Promise<unknown> => {
  if (isRefreshing) {
    return new Promise((resolve, reject) => {
      failedQueue.push({ resolve, reject });
    })
      .then(() => api(originalRequest))
      .catch((err) => Promise.reject(err));
  }

  originalRequest._retry = true;
  isRefreshing = true;

  try {
    await api.post(
      '/auth/refresh',
      {},
      {
        withCredentials: true,
        headers: {
          'Content-Type': 'application/json',
        },
      }
    );

    processQueue(null, 'success');
    isRefreshing = false;
    return api(originalRequest);
  } catch (refreshError: unknown) {
    return handleRefreshError(refreshError);
  }
};

api.interceptors.request.use((config) => {
  const requestUrl = (config.url || '').toLowerCase();
  const requestPath = requestUrl.replace(/^https?:\/\/[^/]+/i, '');

  if (requestUrl.includes('/auth/refresh')) {
    removeEstablishmentSlugHeader(config.headers);
    return config;
  }

  if (requestPath.startsWith('/establishment/') && !shouldMakeApiCalls()) {
    return Promise.reject({
      message: 'Domínio base detectado - chamadas para /establishment/:slug bloqueadas',
      config,
      isAxiosError: false,
    });
  }

  const slug = getEstablishmentSlugFromUrl();

  if (slug) {
    config.headers['x-establishment-slug'] = slug;
  }

  return config;
});

api.interceptors.response.use(
  (response) => {
    // Se for 304 Not Modified e não houver dados, manter a resposta original
    // O browser cache já tem os dados, mas axios não os retorna
    // O código que usa a API deve ter fallback para cache local
    return response;
  },
  async (error) => {
    const originalRequest = error.config;
    const decision = getResponseInterceptorDecision(error, originalRequest);

    switch (decision.type) {
      case 'handle_server_error':
        return handleServerError(error);
      case 'handle_permission_error':
        return handlePermissionError(error);
      case 'handle_token_refresh':
        return handleTokenRefresh(originalRequest);
      default:
        return Promise.reject(error);
    }
  }
);

export { authAPI } from './authService';
export { professionalsAPI } from './professionalsService';
export { worksAPI } from './workService';
export { usersAPI } from './usersService';
export { appointmentsAPI } from './appointmentsService';
export { availabilityAPI } from './availabilityService';
export { productsAPI } from './productsService';
export { invitesAPI } from './invitesService';
export { establishmentAPI } from './establishmentService';
export { waitingListAPI } from './waitingListService';

export default api;
